/**
 * Thin fetch wrapper for the QRAM API.
 *
 * Endpoints live under /api/v1; the Vite dev server proxies that prefix
 * to the backend on :3000, so we just call /api/v1/* from anywhere.
 *
 * The wrapper:
 *   - Sets Content-Type and parses JSON
 *   - Throws on non-2xx responses with the parsed body attached
 *   - Returns response.data (the API uses { data, meta? } envelopes)
 */

/**
 * Optional deployment sub-path (e.g. "/qram") when this app is served
 * behind an nginx location prefix alongside sibling apps, instead of at
 * domain root. Unset (the default) preserves today's root-mounted
 * behavior byte-for-byte — every consumer below degrades to exactly
 * today's literal strings when this is ''.
 */
const rawBasePath = import.meta.env.VITE_BASE_PATH ?? '';
export const BASE_PATH = rawBasePath.replace(/\/+$/, '');

export function apiUrl(path: string): string {
  return `${BASE_PATH}/api/v1${path}`;
}

export interface ApiEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }

  /** The server's error code ({ error: { code } }), e.g. ACCOUNT_LOCKED. */
  get code(): string | undefined {
    return (this.body as { error?: { code?: string } } | null)?.error?.code;
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
    // Slice 9.3: include cookies (session) on every request
    credentials: 'same-origin',
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const res = await fetch(apiUrl(path), init);

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Non-JSON response — uncommon but we surface it as the body
      parsed = text;
    }
  }

  if (!res.ok) {
    // 401 = session expired. Redirect to login immediately so the user
    // doesn't see a frozen page with silent failures in every query.
    if (res.status === 401 && !window.location.pathname.startsWith(`${BASE_PATH}/login`)) {
      window.location.href = `${BASE_PATH}/login`;
      // Return a promise that never resolves — navigation takes over
      return new Promise<never>(() => {});
    }
    const message = (parsed as { error?: { message?: string } })?.error?.message
      ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }

  return (parsed as ApiEnvelope<T>).data ?? (parsed as T);
}

export const api = {
  get:    <T = unknown>(path: string)              => request<T>('GET',    path),
  post:   <T = unknown>(path: string, body?: unknown) => request<T>('POST',   path, body),
  put:    <T = unknown>(path: string, body?: unknown) => request<T>('PUT',    path, body),
  patch:  <T = unknown>(path: string, body?: unknown) => request<T>('PATCH',  path, body),
  delete: <T = unknown>(path: string)              => request<T>('DELETE', path),
};

/**
 * Slice 6b: paginated GET that returns both data and meta. The default
 * `api.get` strips the envelope down to .data, which is convenient for
 * 90% of callers but inconvenient when you need has_more / next_cursor.
 */
/**
 * Every row of a list endpoint whose `limit` is capped at 200 (assets, project assets): pages with
 * `offset` until a short page, so a picker offers all of a large estate, not its first 200 assets.
 */
export async function getAll<T = unknown>(path: string, page = 200): Promise<T[]> {
  const out: T[] = [];
  for (let offset = 0; ; offset += page) {
    const rows = await request<T[]>('GET', `${path}${path.includes('?') ? '&' : '?'}limit=${page}&offset=${offset}`);
    out.push(...rows);
    if (rows.length < page) return out;
  }
}

export async function getWithMeta<T = unknown>(
  path: string,
): Promise<{ data: T; meta: Record<string, unknown> }> {
  const res = await fetch(apiUrl(path), {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
  });
  const text = await res.text();
  const parsed = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message = (parsed as { error?: { message?: string } })?.error?.message
      ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }
  return {
    data: (parsed as ApiEnvelope<T>).data,
    meta: (parsed as ApiEnvelope<T>).meta ?? {},
  };
}

/**
 * Slice 8.5/8.7: POST a CSV body to an admin endpoint. Used by the crypto
 * artifact bulk-import UI to ship a customer's algorithm inventory in
 * one shot. Sends Content-Type: text/csv and parses the JSON response
 * envelope the same way as request().
 */
export async function postCsv<T = unknown>(
  path: string,
  csvBody: string,
  externalSignal?: AbortSignal,
): Promise<T> {
  // 5-minute internal timeout — crypto import does synchronous KB lookups
  // per row. If the caller supplies an externalSignal (e.g. from a Cancel
  // button), aborting it will cancel the fetch immediately.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort('timeout'), 300_000);

  // Wire the external signal into our internal controller so either
  // the timeout OR the user's cancel will abort the fetch.
  let externalAbortHandler: (() => void) | undefined;
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      throw new ApiError(0, 'Import cancelled.', null);
    }
    externalAbortHandler = () => controller.abort('user');
    externalSignal.addEventListener('abort', externalAbortHandler);
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'text/csv' },
      body: csvBody,
      credentials: 'same-origin',
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (externalSignal && externalAbortHandler) {
      externalSignal.removeEventListener('abort', externalAbortHandler);
    }
    if ((err as Error).name === 'AbortError') {
      const reason = (controller.signal as AbortSignal & { reason?: unknown }).reason;
      if (reason === 'user') {
        throw new ApiError(0, 'Import cancelled.', null);
      }
      throw new ApiError(408, 'Import timed out. The server may still be processing — check the list in a moment, or try a smaller batch.', null);
    }
    throw err;
  }
  clearTimeout(timeoutId);
  if (externalSignal && externalAbortHandler) {
    externalSignal.removeEventListener('abort', externalAbortHandler);
  }
  clearTimeout(timeoutId);
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); }
    catch { parsed = text; }
  }
  if (!res.ok) {
    const message = (parsed as { error?: { message?: string } })?.error?.message
      ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }
  return (parsed as ApiEnvelope<T>).data ?? (parsed as T);
}